import { isoDay } from "../../components/FilterBar";
import type { FilterValues } from "../../components/FilterBar";

/**
 * The one line of live information on each Reports category card (owner
 * 2026-10-25: "Active sessions … add number of active sessions … 10 plans are
 * expiring in next 3 days"). Each fact asks its own report once, with the
 * filter below, and reads one figure from the answer. A report that needs a
 * subscriber first (by-user screens) has no fact; its card shows its guidance.
 */
export interface ReportFact {
  /** The filter the fact sends; days are local (IST) days, as the FilterBar sends them. */
  filter: (today: Date) => FilterValues;
  /** The line, from the report's answer; null when the answer says nothing usable. */
  line: (body: unknown) => string | null;
}

type Row = Record<string, unknown>;

/** The answer's rows: a list, or under `rows` (Collections). A Go nil slice arrives as null. */
export function rowsOf(body: unknown): Row[] {
  if (Array.isArray(body)) return body as Row[];
  if (body && typeof body === "object" && Array.isArray((body as Row).rows)) return (body as { rows: Row[] }).rows;
  return [];
}

const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const sum = (rows: Row[], key: string) => rows.reduce((t, r) => t + num(r[key]), 0);
const count = (n: number) => n.toLocaleString("en-IN");
export const rupees = (n: number) => `₹${Math.round(n).toLocaleString("en-IN")}`;
const plural = (n: number, one: string, many: string) => `${count(n)} ${n === 1 ? one : many}`;

function days(today: Date, back: number, ahead = 0): FilterValues {
  const d = (n: number) => isoDay(new Date(today.getFullYear(), today.getMonth(), today.getDate() + n));
  return { start: d(-(back - 1)), end: d(ahead) };
}
const last30 = (t: Date) => days(t, 30);
const none = () => ({});

export const REPORT_FACTS: Record<string, ReportFact> = {
  "active-sessions": { filter: none, line: (b) => `${count(rowsOf(b).length)} online now` },
  "top-usage": {
    filter: last30,
    line: (b) => {
      const top = rowsOf(b)[0];
      return top ? `Top user ${num(top.total_gb).toFixed(1)} GB in 30 days` : "No usage in 30 days";
    },
  },
  "peak-hours": {
    filter: last30,
    line: (b) => {
      const rows = rowsOf(b);
      if (!rows.some((r) => num(r.sessions_started) > 0)) return "No sessions in 30 days";
      const peak = rows.reduce((a, r) => (num(r.sessions_started) > num(a.sessions_started) ? r : a));
      const label =
        typeof peak.label === "string" && peak.label ? peak.label : `${String(num(peak.hour)).padStart(2, "0")}:00`;
      return `Busiest hour ${label}`;
    },
  },
  "never-used": {
    filter: none,
    line: (b) => `${plural(rowsOf(b).length, "paying subscriber", "paying subscribers")}, never connected`,
  },
  "failed-logins": {
    filter: last30,
    line: (b) => {
      const rows = rowsOf(b);
      const still = rows.filter((r) => r.still_failing === true).length;
      return `${plural(rows.length, "username", "usernames")} rejected · ${count(still)} still failing`;
    },
  },
  "active-plans": { filter: none, line: (b) => `${plural(rowsOf(b).length, "plan", "plans")} running` },
  "expiring-plans": {
    filter: (t) => days(t, 1, 3),
    line: (b) => `${plural(rowsOf(b).length, "plan expires", "plans expire")} in the next 3 days`,
  },
  "expired-not-renewed": { filter: last30, line: (b) => `${count(rowsOf(b).length)} lapsed in 30 days, not renewed` },
  "new-subscribers": { filter: last30, line: (b) => `${count(rowsOf(b).length)} new in the last 30 days` },
  "renewals-by-plan": {
    filter: last30,
    line: (b) => {
      const rows = rowsOf(b);
      return `${count(sum(rows, "renewals"))} renewals of ${plural(sum(rows, "recharges"), "recharge", "recharges")} in 30 days`;
    },
  },
  "base-snapshot": {
    filter: none,
    line: (b) => {
      const rows = rowsOf(b);
      return `${count(sum(rows, "active"))} active of ${count(sum(rows, "registered"))} registered`;
    },
  },
  "recharge-log": { filter: last30, line: (b) => `${plural(rowsOf(b).length, "recharge", "recharges")} in 30 days` },
  collections: {
    filter: last30,
    line: (b) => {
      const total = b && typeof b === "object" && !Array.isArray(b) ? num((b as Row).total) : sum(rowsOf(b), "amount");
      return `${rupees(total)} collected in 30 days`;
    },
  },
  "funds-statement": {
    filter: last30,
    line: (b) => {
      const rows = rowsOf(b);
      return rows.length
        ? `${rupees(sum(rows, "closing"))} held by ${plural(rows.length, "sub-operator", "sub-operators")}`
        : "No sub-operator funds";
    },
  },
  "payment-failures": {
    filter: last30,
    line: (b) => `${plural(rowsOf(b).length, "failed payment", "failed payments")} in 30 days`,
  },
  "promo-use": {
    filter: last30,
    line: (b) => `${plural(sum(rowsOf(b), "used"), "code used", "codes used")} in 30 days`,
  },
  "sms-log": { filter: last30, line: (b) => `${count(rowsOf(b).length)} SMS in 30 days` },
  "staff-activity": {
    filter: last30,
    line: (b) => {
      const actors = new Set(rowsOf(b).map((r) => String(r.actor_id ?? r.actor ?? "")));
      actors.delete("");
      return `${plural(actors.size, "login", "logins")} recorded work in 30 days`;
    },
  },
  "tenant-summary": { filter: none, line: (b) => `${plural(rowsOf(b).length, "tenant", "tenants")}` },
};
